import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [avatar, setAvatar] = useState(user?.avatar_url || "");
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await updateProfile({ name, bio, avatar_url: avatar });
    setSaving(false);
    if (res.ok) toast.success("Profile updated"); else toast.error(res.error);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) return toast.error("Please fill in all password fields");
    if (newPassword !== confirmPassword) return toast.error("New passwords do not match");
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setChangingPassword(false); }
  };

  return (
    <div className="pt-16 pb-20 max-w-2xl mx-auto px-6" data-testid="profile-page">
      <h1 className="font-display font-bold text-4xl md:text-5xl text-[var(--text-primary)] tracking-tighter">Your Profile</h1>
      <p className="text-[var(--text-secondary)] mt-2">Manage your account settings.</p>

      <div className="mt-10 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8">
        <div className="flex items-center gap-5 mb-8">
          {avatar ? (
            <img src={avatar} alt={name} className="w-16 h-16 object-cover" onError={(e) => e.target.style.display = "none"} />
          ) : (
            <div className="w-16 h-16 bg-yellow-400 text-black font-bold flex items-center justify-center text-2xl">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-[var(--text-primary)] font-medium">{user?.email}</div>
            <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Role: {user?.role}</div>
          </div>
        </div>
        <form onSubmit={save} className="space-y-5">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Display name</label>
            <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Bio</label>
            <textarea rows={4} data-testid="profile-bio-input" value={bio} onChange={(e) => setBio(e.target.value)}
              className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 resize-none" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Avatar URL (optional)</label>
            <input data-testid="profile-avatar-input" value={avatar} onChange={(e) => setAvatar(e.target.value)} placeholder="https://..."
              className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]" />
          </div>
          <button type="submit" disabled={saving} data-testid="profile-save-button"
            className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 disabled:opacity-50">
            {saving ? "Saving..." : "Save changes"}
          </button>
        </form>
      </div>

      <div className="mt-6 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8">
        <h2 className="font-display font-bold text-xl text-[var(--text-primary)] mb-6">Change Password</h2>
        <form onSubmit={changePassword} className="space-y-5">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password"
              className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password"
              className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password"
              className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]" />
          </div>
          <button type="submit" disabled={changingPassword}
            className="bg-[var(--bg-elevated)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-6 py-3 hover:bg-[var(--bg-panel)] border border-[var(--border-soft)] disabled:opacity-50 transition">
            {changingPassword ? "Changing..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}